import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import {
  AppsEnv,
  LlmProxyKeyCreateResult,
  LlmProxyKeyListItem,
  LlmProxyKeysConfigResponse,
} from '@internal/plugin-llm-proxy-keys-common';

export const llmProxyKeysApiRef = createApiRef<LlmProxyKeysApi>({
  id: 'plugin.llm-proxy-keys.service',
});

export interface LlmProxyKeysApi {
  getConfig(): Promise<LlmProxyKeysConfigResponse>;
  listLocaldevKeys(): Promise<LlmProxyKeyListItem[]>;
  createLocaldevKey(): Promise<LlmProxyKeyCreateResult>;
  rotateLocaldevKey(): Promise<LlmProxyKeyCreateResult>;
  revokeLocaldevKey(): Promise<void>;
  listAdminLocaldevKeys(): Promise<LlmProxyKeyListItem[]>;
  revokeAdminLocaldevKey(email: string): Promise<void>;
  listAppsKeys(env: AppsEnv): Promise<LlmProxyKeyListItem[]>;
  createAppsKey(input: {
    env: AppsEnv;
    componentName: string;
    keyName: string;
  }): Promise<LlmProxyKeyCreateResult>;
  rotateAppsKey(input: {
    env: AppsEnv;
    name: string;
  }): Promise<LlmProxyKeyCreateResult>;
  revokeAppsKey(env: AppsEnv, name: string): Promise<void>;
}

export class LlmProxyKeysClient implements LlmProxyKeysApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
    private readonly identityApi: IdentityApi,
  ) {}

  private async fetch<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('llm-proxy-keys');
    const { token } = await this.identityApi.getCredentials();
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });

    if (!response.ok) {
      const body = await response.text();
      throw new Error(body || response.statusText);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }

  async getConfig(): Promise<LlmProxyKeysConfigResponse> {
    return this.fetch('/v1/config');
  }

  async listLocaldevKeys(): Promise<LlmProxyKeyListItem[]> {
    const response = await this.fetch<{ keys: LlmProxyKeyListItem[] }>(
      '/v1/localdev/keys',
    );
    return response.keys;
  }

  async createLocaldevKey(): Promise<LlmProxyKeyCreateResult> {
    return this.fetch('/v1/localdev/keys', { method: 'POST', body: '{}' });
  }

  async rotateLocaldevKey(): Promise<LlmProxyKeyCreateResult> {
    return this.fetch('/v1/localdev/keys', {
      method: 'POST',
      body: JSON.stringify({ rotate: true }),
    });
  }

  async revokeLocaldevKey(): Promise<void> {
    await this.fetch('/v1/localdev/keys', { method: 'DELETE' });
  }

  async listAdminLocaldevKeys(): Promise<LlmProxyKeyListItem[]> {
    const response = await this.fetch<{ keys: LlmProxyKeyListItem[] }>(
      '/v1/admin/localdev/keys',
    );
    return response.keys;
  }

  async revokeAdminLocaldevKey(email: string): Promise<void> {
    await this.fetch(
      `/v1/admin/localdev/keys/${encodeURIComponent(email)}`,
      { method: 'DELETE' },
    );
  }

  async listAppsKeys(env: AppsEnv): Promise<LlmProxyKeyListItem[]> {
    const response = await this.fetch<{ keys: LlmProxyKeyListItem[] }>(
      `/v1/apps/${env}/keys`,
    );
    return response.keys;
  }

  async createAppsKey(input: {
    env: AppsEnv;
    componentName: string;
    keyName: string;
  }): Promise<LlmProxyKeyCreateResult> {
    return this.fetch(`/v1/apps/${input.env}/keys`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async rotateAppsKey(input: {
    env: AppsEnv;
    name: string;
  }): Promise<LlmProxyKeyCreateResult> {
    return this.fetch(`/v1/apps/${input.env}/keys`, {
      method: 'POST',
      body: JSON.stringify({ ...input, rotate: true }),
    });
  }

  async revokeAppsKey(env: AppsEnv, name: string): Promise<void> {
    await this.fetch(`/v1/apps/${env}/keys/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  }
}
